/**
 * Language utility functions for i18n support.
 *
 * Supported languages:
 *  - 'en' (English) — default
 *  - 'fr' (French)
 *
 * Language resolution chain:
 *   1. User-persisted setting (localStorage key: 'userLanguage')
 *   2. Browser language (navigator.language)
 *   3. Default: 'en'
 */

export const SUPPORTED_LANGUAGES = ['en', 'fr'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';
export const LANGUAGE_STORAGE_KEY = 'userLanguage';

/**
 * Normalizes a BCP-47 language tag to one of the supported languages.
 * e.g. 'fr-CA' → 'fr', 'en-US' → 'en', 'es-ES' → 'en' (fallback)
 */
export function normalizeLanguage(lang: string | null | undefined): SupportedLanguage {
  if (!lang) return DEFAULT_LANGUAGE;

  const base = lang.toLowerCase().split('-')[0];

  if (base === 'fr') return 'fr';
  if (base === 'en') return 'en';

  return DEFAULT_LANGUAGE;
}

/**
 * Reads the persisted user language from localStorage.
 * Returns null if none is set.
 */
export function getPersistedLanguage(): SupportedLanguage | null {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
      return stored as SupportedLanguage;
    }
  } catch {
    // localStorage may be unavailable in some environments
  }
  return null;
}

/**
 * Persists the user's language preference to localStorage.
 */
export function setPersistedLanguage(lang: SupportedLanguage): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Resolves the active language using the full priority chain:
 *   user setting → browser language → 'en'
 */
export function resolveLanguage(): SupportedLanguage {
  const persisted = getPersistedLanguage();
  if (persisted) return persisted;

  const browserLang =
    (typeof navigator !== 'undefined' && navigator.language) || null;
  return normalizeLanguage(browserLang);
}
